import { useThemeMode } from '@/contexts/ThemeContext'

interface TOSContentProps {
  isMobile?: boolean
}

export default function TOSContent({ isMobile = false }: TOSContentProps) {
  const { colors } = useThemeMode()

  const headingSize = isMobile ? '14px' : '20px'
  const subHeadingSize = isMobile ? '13px' : '18px'
  const textSize = isMobile ? '12px' : '14px'

  return (
    <div style={{
      padding: isMobile ? '16px' : '24px',
      height: '100%',
      overflowY: 'auto',
      color: colors.primaryText,
      lineHeight: 1.6,
      fontSize: textSize,
    }}>
      <p><strong>Last Updated: November 20, 2025</strong></p>

      <h2 style={{ marginTop: isMobile ? '16px' : '24px', marginBottom: isMobile ? '8px' : '12px', fontSize: headingSize, fontWeight: 'bold' }}>
        AGREEMENT TO TERMS
      </h2>
      <p>
        We are Plastic Labs Inc. ("Company," "we," "us," "our"), a company registered in Delaware, United States at 169 Madison Ave, STE 2703, New York, NY 10016.
      </p>
      <p>
        We operate the website <a href="https://honcho.chat" target="_blank" rel="noopener noreferrer" style={{ color: colors.primaryAccent, textDecoration: 'underline' }}>https://honcho.chat</a> (the "Site"), as well as any other related products and services that refer or link to these legal terms (the "Legal Terms") (collectively, the "Services").
      </p>
      <p>
        Honcho Chat is an AI assistant platform powered by Honcho's state-of-the-art memory and reasoning infrastructure. Users can chat with AI assistants that build rich personal context over time, create and sell digital artifacts, manage cryptocurrency wallets, and customize their experience with themes and identities.
      </p>
      <p>
        You can contact us by email at hello@plasticlabs.ai or by mail to 169 Madison Ave, STE 2703, New York, NY 10016, United States.
      </p>
      <p>
        These Legal Terms constitute a legally binding agreement made between you, whether personally or on behalf of an entity ("you"), and Plastic Labs Inc., concerning your access to and use of the Services. You agree that by accessing the Services, you have read, understood, and agreed to be bound by all of these Legal Terms. IF YOU DO NOT AGREE WITH ALL OF THESE LEGAL TERMS, THEN YOU ARE EXPRESSLY PROHIBITED FROM USING THE SERVICES AND YOU MUST DISCONTINUE USE IMMEDIATELY.
      </p>
      <p>
        The Services are intended for users who are at least 18 years old. Persons under the age of 18 are not permitted to use or register for the Services.
      </p>

      <h2 style={{ marginTop: '24px', marginBottom: '12px', fontSize: '18px', fontWeight: 'bold' }}>
        TABLE OF CONTENTS
      </h2>
      <ol style={{ paddingLeft: '20px' }}>
        <li>OUR SERVICES</li>
        <li>INTELLECTUAL PROPERTY RIGHTS</li>
        <li>USER REPRESENTATIONS</li>
        <li>USER REGISTRATION</li>
        <li>PURCHASES AND PAYMENT</li>
        <li>PROHIBITED ACTIVITIES</li>
        <li>USER GENERATED CONTRIBUTIONS</li>
        <li>CONTRIBUTION LICENSE</li>
        <li>THIRD-PARTY SERVICES</li>
        <li>SERVICES MANAGEMENT</li>
        <li>PRIVACY POLICY</li>
        <li>COPYRIGHT INFRINGEMENTS</li>
        <li>TERM AND TERMINATION</li>
        <li>MODIFICATIONS AND INTERRUPTIONS</li>
        <li>GOVERNING LAW</li>
        <li>DISPUTE RESOLUTION</li>
        <li>DISCLAIMER</li>
        <li>LIMITATIONS OF LIABILITY</li>
        <li>INDEMNIFICATION</li>
        <li>USER DATA</li>
        <li>ELECTRONIC COMMUNICATIONS</li>
        <li>MISCELLANEOUS</li>
        <li>HONCHO MEMORY SYSTEM</li>
        <li>CRYPTOCURRENCY AND WALLETS</li>
        <li>MARKETPLACE TERMS</li>
        <li>AI-GENERATED CONTENT</li>
        <li>CONTACT US</li>
      </ol>

      <h2 style={{ marginTop: '24px', marginBottom: '12px', fontSize: '18px', fontWeight: 'bold' }}>
        1. OUR SERVICES
      </h2>
      <p>
        The information provided when using the Services is not intended for distribution to or use by any person or entity in any jurisdiction or country where such distribution or use would be contrary to law or regulation or which would subject us to any registration requirement within such jurisdiction or country.
      </p>
      <p>
        The Services are not tailored to comply with industry-specific regulations (Health Insurance Portability and Accountability Act (HIPAA), Federal Information Security Management Act (FISMA), etc.), so if your interactions would be subjected to such laws, you may not use the Services.
      </p>
      <p>
        We provide software interface tools only and do not provide financial, investment, or advisory services. Users interact directly with blockchain networks and smart contracts. We do not facilitate, execute, or control transactions between parties.
      </p>

      <h2 style={{ marginTop: '24px', marginBottom: '12px', fontSize: '18px', fontWeight: 'bold' }}>
        2. INTELLECTUAL PROPERTY RIGHTS
      </h2>
      <h3 style={{ marginTop: '16px', marginBottom: '8px', fontSize: '16px', fontWeight: 'bold' }}>
        Our intellectual property
      </h3>
      <p>
        We are the owner or the licensee of all intellectual property rights in our Services, including all source code, databases, functionality, software, website designs, audio, video, text, photographs, and graphics in the Services (collectively, the "Content"), as well as the trademarks, service marks, and logos contained therein (the "Marks").
      </p>
      <p>
        Our Content and Marks are protected by copyright and trademark laws and treaties in the United States and around the world.
      </p>

      <h3 style={{ marginTop: '16px', marginBottom: '8px', fontSize: '16px', fontWeight: 'bold' }}>
        Your use of our Services
      </h3>
      <p>Subject to your compliance with these Legal Terms, we grant you a non-exclusive, non-transferable, revocable license to:</p>
      <ul style={{ paddingLeft: '20px', marginTop: '8px', marginBottom: '8px' }}>
        <li>Access the Services</li>
        <li>Create and manage AI assistant identities</li>
        <li>Generate and share artifacts and themes</li>
        <li>Interact with the marketplace</li>
      </ul>

      <h3 style={{ marginTop: '16px', marginBottom: '8px', fontSize: '16px', fontWeight: 'bold' }}>
        Your submissions
      </h3>
      <p>
        By creating content through the Services, you grant us a license to use, display, and distribute such content as necessary to provide the Services. You retain ownership of your original content.
      </p>

      <h2 style={{ marginTop: '24px', marginBottom: '12px', fontSize: '18px', fontWeight: 'bold' }}>
        3. USER REPRESENTATIONS
      </h2>
      <p>By using the Services, you represent and warrant that:</p>
      <ol style={{ paddingLeft: '20px', marginTop: '8px', marginBottom: '8px' }}>
        <li>All registration information you submit will be true, accurate, current, and complete</li>
        <li>You have the legal capacity and agree to comply with these Legal Terms</li>
        <li>You are not a minor in the jurisdiction in which you reside</li>
        <li>You will not use the Services for any illegal or unauthorized purpose</li>
        <li>You understand the risks associated with cryptocurrency and blockchain technology</li>
        <li>You are solely responsible for the security of your wallet and private keys</li>
        <li>Your use of the Services will not violate any applicable law or regulation</li>
      </ol>

      <h2 style={{ marginTop: '24px', marginBottom: '12px', fontSize: '18px', fontWeight: 'bold' }}>
        4. USER REGISTRATION
      </h2>
      <p>
        You are required to register to use the Services. You agree to keep your password confidential and will be responsible for all use of your account and password. We reserve the right to remove, reclaim, or change a username you select if we determine, in our sole discretion, that such username is inappropriate, obscene, or otherwise objectionable.
      </p>

      <h2 style={{ marginTop: '24px', marginBottom: '12px', fontSize: '18px', fontWeight: 'bold' }}>
        5. PURCHASES AND PAYMENT
      </h2>
      <h3 style={{ marginTop: '16px', marginBottom: '8px', fontSize: '16px', fontWeight: 'bold' }}>
        Payment Methods
      </h3>
      <p>We accept Base USDC for marketplace transactions.</p>

      <h3 style={{ marginTop: '16px', marginBottom: '8px', fontSize: '16px', fontWeight: 'bold' }}>
        Wallet Management
      </h3>
      <ul style={{ paddingLeft: '20px', marginTop: '8px', marginBottom: '8px' }}>
        <li>You are solely responsible for your wallet security</li>
        <li>We do not custody or control your funds</li>
        <li>Lost private keys cannot be recovered</li>
        <li>All blockchain transactions are final and irreversible</li>
      </ul>

      <h3 style={{ marginTop: '16px', marginBottom: '8px', fontSize: '16px', fontWeight: 'bold' }}>
        Non-Custodial Operations
      </h3>
      <p>
        We operate on a fully non-custodial basis. Users maintain exclusive control of their wallets and private keys. We merely provide the interface to interact with autonomous smart contracts.
      </p>

      <h2 style={{ marginTop: '24px', marginBottom: '12px', fontSize: '18px', fontWeight: 'bold' }}>
        6. PROHIBITED ACTIVITIES
      </h2>
      <p>You may not access or use the Services for any purpose other than that for which we make the Services available. As a user of the Services, you agree not to:</p>
      <ul style={{ paddingLeft: '20px', marginTop: '8px', marginBottom: '8px' }}>
        <li>Systematically retrieve data to create compilations without permission</li>
        <li>Trick, defraud, or mislead users</li>
        <li>Circumvent security features</li>
        <li>Disparage, tarnish, or harm us or the Services</li>
        <li>Harass, abuse, or harm another person</li>
        <li>Make improper use of support services</li>
        <li>Use the Services in violation of laws or regulations</li>
        <li>Upload viruses or malicious code</li>
        <li>Attempt unauthorized automated use of the system</li>
        <li>Impersonate another user or person</li>
        <li>Copy or adapt the Services' software</li>
        <li>Sell or transfer your profile</li>
        <li>Use the Services for unauthorized commercial purposes</li>
        <li>Manipulate the memory system or Representation data</li>
        <li>Attempt to extract or reverse-engineer Honcho's reasoning algorithms</li>
        <li>Create false or misleading AI assistant identities</li>
        <li>Use stolen or fraudulently obtained cryptocurrency</li>
        <li>Engage in money laundering or financial crimes</li>
        <li>Violate export controls or sanctions</li>
        <li>Distribute content that promotes illegal activities</li>
        <li>Share private information without consent</li>
        <li>Attempt to manipulate marketplace rankings or prices</li>
      </ul>

      <h2 style={{ marginTop: '24px', marginBottom: '12px', fontSize: '18px', fontWeight: 'bold' }}>
        7. USER GENERATED CONTRIBUTIONS
      </h2>
      <p>The Services allow you to create, submit, and share content including:</p>
      <ul style={{ paddingLeft: '20px', marginTop: '8px', marginBottom: '8px' }}>
        <li>Messages and conversations with AI assistants</li>
        <li>Custom AI identities and system prompts</li>
        <li>Artifacts (code, applications, tools)</li>
        <li>Themes and visual customizations</li>
        <li>Profile information and descriptions</li>
      </ul>
      <p>You are responsible for your contributions and warrant that:</p>
      <ul style={{ paddingLeft: '20px', marginTop: '8px', marginBottom: '8px' }}>
        <li>You own or have rights to use the content</li>
        <li>Your content does not infringe on third-party rights</li>
        <li>Your content is not false, misleading, or harmful</li>
        <li>Your content complies with all applicable laws</li>
      </ul>

      <h2 style={{ marginTop: '24px', marginBottom: '12px', fontSize: '18px', fontWeight: 'bold' }}>
        8. CONTRIBUTION LICENSE
      </h2>
      <p>By posting contributions to the Services, you grant us a worldwide, non-exclusive, royalty-free license to:</p>
      <ul style={{ paddingLeft: '20px', marginTop: '8px', marginBottom: '8px' }}>
        <li>Host, store, and display your content</li>
        <li>Process content through our AI and memory systems</li>
        <li>Enable marketplace transactions for your listed items</li>
        <li>Generate embeddings and reasoning outputs</li>
        <li>Provide the Services to you and other users</li>
      </ul>
      <p>
        You retain ownership of your original content. For content sold on the marketplace, purchasers receive the rights specified in the marketplace listing.
      </p>

      <h2 style={{ marginTop: '24px', marginBottom: '12px', fontSize: '18px', fontWeight: 'bold' }}>
        17. DISCLAIMER
      </h2>
      <p>
        THE SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE." WE DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
      </p>
      <p>WE DO NOT WARRANT:</p>
      <ul style={{ paddingLeft: '20px', marginTop: '8px', marginBottom: '8px' }}>
        <li>Uninterrupted or error-free service</li>
        <li>Accuracy of AI-generated content</li>
        <li>Security of blockchain transactions</li>
        <li>Availability of third-party services</li>
        <li>Suitability of content for any purpose</li>
      </ul>

      <h2 style={{ marginTop: '24px', marginBottom: '12px', fontSize: '18px', fontWeight: 'bold' }}>
        18. LIMITATIONS OF LIABILITY
      </h2>
      <p>
        TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE ARE NOT LIABLE FOR INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING LOST PROFITS, DATA LOSS, OR CRYPTOCURRENCY LOSSES.
      </p>
      <p>
        OUR TOTAL LIABILITY SHALL NOT EXCEED $100 OR THE AMOUNT OF FEES PAID BY YOU IN THE PRECEDING 12 MONTHS, WHICHEVER IS GREATER.
      </p>

      <h2 style={{ marginTop: '24px', marginBottom: '12px', fontSize: '18px', fontWeight: 'bold' }}>
        23. HONCHO MEMORY SYSTEM
      </h2>
      <h3 style={{ marginTop: '16px', marginBottom: '8px', fontSize: '16px', fontWeight: 'bold' }}>
        Representation Building
      </h3>
      <p>The Services use Honcho to build a comprehensive Representation of your identity through:</p>
      <ul style={{ paddingLeft: '20px', marginTop: '8px', marginBottom: '8px' }}>
        <li>Continuous learning from interactions</li>
        <li>Semantic analysis and reasoning</li>
        <li>Pattern recognition</li>
        <li>Context accumulation</li>
      </ul>

      <h3 style={{ marginTop: '16px', marginBottom: '8px', fontSize: '16px', fontWeight: 'bold' }}>
        Data Permanence
      </h3>
      <p>
        Information processed by Honcho becomes part of your permanent Representation and cannot be selectively deleted.
      </p>

      <h3 style={{ marginTop: '16px', marginBottom: '8px', fontSize: '16px', fontWeight: 'bold' }}>
        Future Portability
      </h3>
      <p>
        Representations may become portable to other Honcho-powered applications, subject to additional terms.
      </p>

      <h2 style={{ marginTop: '24px', marginBottom: '12px', fontSize: '18px', fontWeight: 'bold' }}>
        24. CRYPTOCURRENCY AND WALLETS
      </h2>
      <h3 style={{ marginTop: '16px', marginBottom: '8px', fontSize: '16px', fontWeight: 'bold' }}>
        Wallet Generation
      </h3>
      <ul style={{ paddingLeft: '20px', marginTop: '8px', marginBottom: '8px' }}>
        <li>Wallets are generated using secure cryptographic methods</li>
        <li>Private keys are encrypted using your device's passkey system</li>
        <li>We cannot recover lost private keys</li>
      </ul>

      <h3 style={{ marginTop: '16px', marginBottom: '8px', fontSize: '16px', fontWeight: 'bold' }}>
        Transaction Risks
      </h3>
      <p>You acknowledge and accept:</p>
      <ul style={{ paddingLeft: '20px', marginTop: '8px', marginBottom: '8px' }}>
        <li>All transactions are irreversible</li>
        <li>Network fees are variable and beyond our control</li>
        <li>Smart contracts may contain bugs or vulnerabilities</li>
        <li>Cryptocurrency values fluctuate</li>
        <li>Regulatory changes may affect availability</li>
      </ul>

      <h3 style={{ marginTop: '16px', marginBottom: '8px', fontSize: '16px', fontWeight: 'bold' }}>
        Your Responsibilities
      </h3>
      <ul style={{ paddingLeft: '20px', marginTop: '8px', marginBottom: '8px' }}>
        <li>Secure storage of private keys</li>
        <li>Compliance with tax obligations</li>
        <li>Verification of transaction details</li>
        <li>Understanding blockchain technology risks</li>
      </ul>

      <h2 style={{ marginTop: '24px', marginBottom: '12px', fontSize: '18px', fontWeight: 'bold' }}>
        27. CONTACT US
      </h2>
      <p>For questions about these Terms:</p>
      <p>
        <strong>Plastic Labs, Inc.</strong><br />
        Email: hello@plasticlabs.ai<br />
        Address: 169 Madison Ave, STE 2703<br />
        New York, NY 10016<br />
        United States
      </p>

      <div style={{
        marginTop: '32px',
        marginBottom: '16px',
        padding: '16px',
        backgroundColor: colors.secondaryBg,
        borderRadius: '8px',
        border: `1px solid ${colors.primaryBorder}`
      }}>
        <p style={{ margin: 0, fontWeight: 'bold', textAlign: 'center' }}>
          BY USING HONCHO CHAT, YOU ACKNOWLEDGE THAT YOU HAVE READ, UNDERSTOOD, AND AGREE TO BE BOUND BY THESE TERMS OF SERVICE.
        </p>
      </div>
    </div>
  )
}
